import React from 'react';
import { MapPinIcon, TriangleAlertIcon, UserCheckIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useSafety } from '../../contexts/SafetyContext';
import { responderForCampus } from '../../data/safety';
import type { AlertState, EmergencyAlert } from '../../types/safety';
const STATE: Record<AlertState, {
  label: string;
  tone: 'ok' | 'warn' | 'danger' | 'brand' | 'neutral';
  meaning: string;
}> = {
  sending: {
    label: 'Sending',
    tone: 'brand',
    meaning: 'Still leaving your device.'
  },
  recorded: {
    label: 'Recorded, not yet seen',
    tone: 'warn',
    meaning: 'Campuzon holds it and is relaying it. Nobody has opened it yet.'
  },
  delivered: {
    label: 'Delivered, not yet opened',
    tone: 'warn',
    meaning: 'It is on the responder desk. No one has acknowledged it yet.'
  },
  acknowledged: {
    label: 'Acknowledged',
    tone: 'brand',
    meaning: 'A named responder has opened your alert.'
  },
  responding: {
    label: 'Responding',
    tone: 'brand',
    meaning: 'Someone is on the way or in contact.'
  },
  closed: {
    label: 'Closed',
    tone: 'ok',
    meaning: 'The responder recorded an outcome.'
  },
  cancelled: {
    label: 'Withdrawn',
    tone: 'neutral',
    meaning: 'You withdrew this alert.'
  },
  undeliverable: {
    label: 'Not delivered',
    tone: 'danger',
    meaning: 'Campuzon could not reach a responder on this campus. Call security directly.'
  }
};

/** The student's own view of an alert. It never overstates progress. */
export function AlertTracker({
  alert
}: {
  alert: EmergencyAlert;
}) {
  const {
    cancel
  } = useSafety();
  const responder = responderForCampus(alert.campusId);
  const state = STATE[alert.state];
  const live = !['closed', 'cancelled'].includes(alert.state);
  return <Panel title={`${alert.reference} · ${state.label}`} description={state.meaning} action={live ? <Button size="sm" variant="ghost" onClick={() => cancel(alert.id, `@${alert.raisedBy.handle}`)} data-id="element-4215" data-mp="1m1xlb81m8xdj">
            I am safe — withdraw
          </Button> : undefined} data-id="element-4214" data-mp="1r0ic6guza4pb">
      <div className="space-y-4" data-id="element-4216" data-mp="1ggom2l4gt5xn">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4217" data-mp="12fc7ad11jadc">
          <Badge tone={state.tone} data-id="element-4218" data-mp="12gx1g1jg0rao">{state.label}</Badge>
          <span className="cz-num text-body-sm text-ink-muted" data-id="element-4219" data-mp="17e73qhrxkxfx">Raised {alert.raisedAt}</span>
        </div>

        {alert.state === 'undeliverable' && <p className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3.5 py-3 text-body-sm text-ink" data-id="element-4220" data-mp="1v4t6pun2vrj2">
            <TriangleAlertIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-danger-700" data-id="element-4221" data-mp="1le847w4aa2uv" />
            Nobody was notified. Call {responder?.phone ?? '112'} now. This record exists so the gap
            is visible, not so it looks handled.
          </p>}

        {responder && alert.responder && <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-4222" data-mp="2iu42expkmvqc">
            <UserCheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4223" data-mp="2ivf1ik82nfb4" />
            <span data-id="element-4224" data-mp="7ca9994fhjps">
              <span className="font-medium" data-id="element-4225" data-mp="2fs7mi4ea1ho">{alert.responder}</span> is handling this.
            </span>
          </p>}

        {alert.placeHint && <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-4226" data-mp="g65fyll77i7j">
            <MapPinIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4227" data-mp="c6eiy052w5uk" />
            {alert.placeHint}
            {alert.coordinates ? ` · location shared, accurate to about ${alert.coordinates.accuracyMetres} m` : ' · precise location not shared'}
          </p>}

        <div data-id="element-4228" data-mp="3tneh4rdsoy6g">
          <p className="text-body-sm font-semibold text-ink" data-id="element-4229" data-mp="3tjqnls8cobrq">Everything that has happened</p>
          <ol className="mt-2 space-y-2 border-l-2 border-line pl-4" data-id="element-4230" data-mp="3olffblxpe9bm">
            {alert.timeline.map((event, index) => <li key={`${event.at}-${index}`} data-id="element-4231" data-mp="13wjbim2zrz6v" data-mpi={index}>
                <p className="text-body-sm text-ink" data-id="element-4232" data-mp="13vq037t38lie">{event.label}</p>
                <p className="cz-num text-caption text-ink-subtle" data-id="element-4233" data-mp="1z77nxi7tbzek">
                  {event.at} · {event.actor}
                </p>
              </li>)}
          </ol>
        </div>

        {alert.outcome && <div className="rounded-md border border-line bg-canvas px-3.5 py-3" data-id="element-4234" data-mp="293u61z8r8ccw">
            <p className="text-body-sm font-semibold text-ink" data-id="element-4235" data-mp="2e4i0kuw6zbbv">Outcome</p>
            <p className="mt-1 max-w-[70ch] text-body-sm text-ink" data-id="element-4236" data-mp="2i5k07cu0tped">{alert.outcome}</p>
          </div>}
      </div>
    </Panel>;
}